// โครงหน้าแอป (แบบ Lorka): แถบซ้าย = แชทใหม่ / เมนูหลัก / ประวัติแชท / การ์ดอัปเกรด / บัญชี  ·  ขวา = เนื้อหา
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { chats as chatSvc } from "../../services/chats";
import { fmtTokens } from "../../services/pricing";
import { IS_MOCK } from "../../services/config";
import { Avatar, Badge, Icon, IconButton, Logo, Progress } from "../ui";

export const CHATS_EVENT = "mixai:chats";
export const notifyChats = () => window.dispatchEvent(new Event(CHATS_EVENT));

const NAV = [
  { to: "/chat", icon: "chat", label: "แชท AI" },
  { to: "/image", icon: "image", label: "สร้างภาพ", soon: true },
  { to: "/video", icon: "video", label: "สร้างวิดีโอ", soon: true },
  { to: "/tools", icon: "wand", label: "เครื่องมือ AI" },
  { to: "/models", icon: "layers", label: "โมเดลทั้งหมด" },
];

const groupOf = (iso) => {
  const d = new Date(iso);
  const days = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
  if (days <= 0) return "วันนี้";
  if (days === 1) return "เมื่อวาน";
  if (days < 7) return "7 วันที่ผ่านมา";
  if (days < 30) return "30 วันที่ผ่านมา";
  return "เก่ากว่านั้น";
};

function ChatHistory({ onPick }) {
  const { profile } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [menu, setMenu] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const load = useCallback(() => {
    if (!profile?.uid) return setList([]);
    chatSvc.list(profile.uid).then(setList).catch(() => {});
  }, [profile?.uid]);
  useEffect(() => {
    load();
    window.addEventListener(CHATS_EVENT, load);
    return () => window.removeEventListener(CHATS_EVENT, load);
  }, [load]);
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menu]);

  const groups = useMemo(() => {
    const out = [];
    const f = q.trim().toLowerCase();
    list
      .filter((c) => !f || String(c.title || "").toLowerCase().includes(f))
      .forEach((c) => {
        const g = c.pinned ? "ปักหมุด" : groupOf(c.updatedAt);
        let bucket = out.find((x) => x.name === g);
        if (!bucket) out.push((bucket = { name: g, items: [] }));
        bucket.items.push(c);
      });
    return out;
  }, [list, q]);

  if (!profile) {
    return (
      <div className="side-guest">
        <Icon name="history" size={20} />
        <span>เข้าสู่ระบบเพื่อบันทึกประวัติแชท</span>
      </div>
    );
  }

  const act = async (kind, c) => {
    setMenu(null);
    if (kind === "rename") {
      const t = window.prompt("ตั้งชื่อแชทใหม่", c.title);
      if (t && t.trim()) await chatSvc.rename(profile.uid, c.id, t.trim());
    } else if (kind === "pin") await chatSvc.pin(profile.uid, c.id, !c.pinned);
    else if (kind === "delete") {
      if (!window.confirm(`ลบแชท "${c.title}" ?`)) return;
      await chatSvc.remove(profile.uid, c.id);
      if (location.pathname === `/chat/${c.id}`) navigate("/chat");
    }
    load();
  };

  return (
    <div className="side-history">
      <div className="side-search">
        <Icon name="search" size={15} />
        <input placeholder="ค้นหาแชท" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {!list.length ? <div className="side-empty">ยังไม่มีประวัติแชท</div> : null}
      {groups.map((g) => (
        <div key={g.name} className="side-group">
          <div className="side-group-title">{g.name}</div>
          {g.items.map((c) => (
            <div key={c.id} className="side-chat-row">
              <NavLink to={`/chat/${c.id}`} className="side-chat" onClick={onPick} title={c.title}>
                {c.pinned ? <Icon name="pin" size={13} /> : null}
                <span>{c.title || "แชทใหม่"}</span>
              </NavLink>
              <button
                type="button"
                className="side-chat-more"
                aria-label="ตัวเลือก"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenu(menu === c.id ? null : c.id);
                }}
              >
                <Icon name="more" size={16} />
              </button>
              {menu === c.id ? (
                <div className="menu menu-right" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => act("pin", c)}><Icon name="pin" size={15} />{c.pinned ? "เลิกปักหมุด" : "ปักหมุด"}</button>
                  <button onClick={() => act("rename", c)}><Icon name="edit" size={15} />เปลี่ยนชื่อ</button>
                  <button className="danger" onClick={() => act("delete", c)}><Icon name="trash" size={15} />ลบ</button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function UserMenu({ onClose }) {
  const { profile, isSuperUser, logout, balance } = useAuth();
  const { pref, setTheme } = useTheme();
  const navigate = useNavigate();
  return (
    <div className="menu menu-up user-menu" onClick={(e) => e.stopPropagation()}>
      <div className="menu-head">
        <Avatar name={profile.displayName || profile.email} size={36} />
        <div className="min0">
          <div className="ellipsis b">{profile.displayName}</div>
          <div className="ellipsis faint small">{profile.email}</div>
        </div>
      </div>
      <Link to="/account" onClick={onClose}><Icon name="wallet" size={16} />แพ็กเกจและการใช้งาน{balance ? <span className="menu-meta">{fmtTokens(balance.total)}</span> : null}</Link>
      <Link to="/pricing" onClick={onClose}><Icon name="crown" size={16} />อัปเกรด / ซื้อ token</Link>
      <Link to="/settings" onClick={onClose}><Icon name="settings" size={16} />ตั้งค่า</Link>
      {isSuperUser ? <Link to="/admin" onClick={onClose}><Icon name="shield" size={16} />หลังบ้าน (super_user)</Link> : null}
      <div className="menu-theme">
        {[["light", "sun", "สว่าง"], ["dark", "moon", "มืด"], ["system", "monitor", "ตามระบบ"]].map(([v, ic, l]) => (
          <button key={v} className={pref === v ? "is-active" : ""} onClick={() => setTheme(v)} title={l}>
            <Icon name={ic} size={15} />
            <span>{l}</span>
          </button>
        ))}
      </div>
      <button
        className="danger"
        onClick={async () => {
          onClose();
          await logout();
          navigate("/login");
        }}
      >
        <Icon name="logout" size={16} />ออกจากระบบ
      </button>
    </div>
  );
}

export default function AppShell() {
  const { profile, balance } = useAuth();
  const { theme, toggle } = useTheme();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("mixai:side") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const userRef = useRef(null);

  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    if (!userOpen) return undefined;
    const close = () => setUserOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [userOpen]);
  const toggleSide = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem("mixai:side", c ? "0" : "1");
      } catch (e) {
        /* ข้าม */
      }
      return !c;
    });
  };

  const planName = balance?.plan?.name || "Free";
  const quotaPct = balance && balance.limit ? balance.used / balance.limit : 0;

  return (
    <div className={"shell" + (collapsed ? " is-collapsed" : "") + (mobileOpen ? " is-mobile-open" : "")}>
      <aside className="side">
        <div className="side-top">
          <Link to="/chat" className="side-logo" aria-label="MixAI">
            <Logo size={30} text={!collapsed} />
          </Link>
          <IconButton icon="panel" label={collapsed ? "ขยายแถบข้าง" : "ย่อแถบข้าง"} onClick={toggleSide} className="side-toggle" />
          <IconButton icon="x" label="ปิดเมนู" onClick={() => setMobileOpen(false)} className="side-close" />
        </div>

        <button className="side-new" onClick={() => navigate("/chat", { state: { fresh: Date.now() } })} title="แชทใหม่">
          <Icon name="plus" size={18} stroke={2.2} />
          <span>แชทใหม่</span>
          <kbd>⌘K</kbd>
        </button>

        <nav className="side-nav">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className="side-link" title={n.label} end={n.to === "/chat" ? false : undefined}>
              <Icon name={n.icon} size={19} />
              <span>{n.label}</span>
              {n.soon ? <em className="side-soon">เร็วๆ นี้</em> : null}
            </NavLink>
          ))}
        </nav>

        <div className="side-scroll">
          <ChatHistory onPick={() => setMobileOpen(false)} />
        </div>

        <div className="side-bottom">
          {profile && balance && balance.planId !== "max" ? (
            <Link to="/pricing" className="upgrade-card">
              <div className="upgrade-row">
                <span className="upgrade-ic"><Icon name="crown" size={16} /></span>
                <b>{balance.planId === "free" ? "อัปเกรดเป็น Pro" : "เพิ่มโควตา"}</b>
              </div>
              <div className="upgrade-meter">
                <span>โควตาเดือนนี้</span>
                <span>{fmtTokens(balance.used)} / {fmtTokens(balance.limit)}</span>
              </div>
              <Progress value={quotaPct} height={6} />
              <div className="upgrade-sub">ปลดล็อกโมเดลมากขึ้น ปรับโควตาได้ถึง 50M / เดือน</div>
            </Link>
          ) : null}

          {profile ? (
            <div className="side-user" ref={userRef}>
              <button
                className="side-user-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setUserOpen((o) => !o);
                }}
              >
                <Avatar name={profile.displayName || profile.email} size={34} />
                <span className="side-user-info">
                  <span className="ellipsis b">{profile.displayName || profile.email}</span>
                  <span className="side-user-plan">
                    <Badge tone={balance?.planId === "free" ? "neutral" : "brand"} icon={balance?.planId === "free" ? undefined : "crown"}>{planName}</Badge>
                    {balance ? <span className="faint small">{fmtTokens(balance.total)} token</span> : null}
                  </span>
                </span>
                <Icon name="more" size={16} />
              </button>
              {userOpen ? <UserMenu onClose={() => setUserOpen(false)} /> : null}
            </div>
          ) : (
            <div className="side-auth">
              <Link to="/login" className="btn btn-primary btn-md btn-block">เข้าสู่ระบบ</Link>
              <Link to="/register" className="btn btn-default btn-md btn-block">สมัครฟรี</Link>
            </div>
          )}
        </div>
      </aside>
      <div className="side-scrim" onClick={() => setMobileOpen(false)} />

      <div className="main">
        <header className="topbar">
          <IconButton icon="menu" label="เมนู" onClick={() => setMobileOpen(true)} className="topbar-menu" />
          <div className="topbar-slot" id="topbar-slot" />
          <div className="topbar-right">
            {IS_MOCK ? <Badge tone="warn" icon="info">โหมดทดลอง</Badge> : null}
            {profile && balance ? (
              <Link to="/account" className="token-chip" title="token คงเหลือ (โควตา + กระเป๋า)">
                <Icon name="zap" size={15} />
                <b>{fmtTokens(balance.total)}</b>
                <span>token</span>
              </Link>
            ) : null}
            <IconButton icon={theme === "dark" ? "sun" : "moon"} label={theme === "dark" ? "โหมดสว่าง" : "โหมดมืด"} onClick={toggle} />
            {!profile ? (
              <Link to="/login" className="btn btn-primary btn-sm">
                เข้าสู่ระบบ
              </Link>
            ) : null}
          </div>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
